import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { get, post, patch, del } from '../../util/api';

// ================ Action types ================ //

export const FETCH_VOUCHERS_REQUEST = 'app/AdminVouchersPage/FETCH_VOUCHERS_REQUEST';
export const FETCH_VOUCHERS_SUCCESS = 'app/AdminVouchersPage/FETCH_VOUCHERS_SUCCESS';
export const FETCH_VOUCHERS_ERROR = 'app/AdminVouchersPage/FETCH_VOUCHERS_ERROR';

export const CREATE_VOUCHER_REQUEST = 'app/AdminVouchersPage/CREATE_VOUCHER_REQUEST';
export const CREATE_VOUCHER_SUCCESS = 'app/AdminVouchersPage/CREATE_VOUCHER_SUCCESS';
export const CREATE_VOUCHER_ERROR = 'app/AdminVouchersPage/CREATE_VOUCHER_ERROR';

export const UPDATE_VOUCHER_REQUEST = 'app/AdminVouchersPage/UPDATE_VOUCHER_REQUEST';
export const UPDATE_VOUCHER_SUCCESS = 'app/AdminVouchersPage/UPDATE_VOUCHER_SUCCESS';
export const UPDATE_VOUCHER_ERROR = 'app/AdminVouchersPage/UPDATE_VOUCHER_ERROR';

export const DELETE_VOUCHER_REQUEST = 'app/AdminVouchersPage/DELETE_VOUCHER_REQUEST';
export const DELETE_VOUCHER_SUCCESS = 'app/AdminVouchersPage/DELETE_VOUCHER_SUCCESS';
export const DELETE_VOUCHER_ERROR = 'app/AdminVouchersPage/DELETE_VOUCHER_ERROR';

export const TOGGLE_VOUCHER_STATUS_REQUEST = 'app/AdminVouchersPage/TOGGLE_VOUCHER_STATUS_REQUEST';
export const TOGGLE_VOUCHER_STATUS_SUCCESS = 'app/AdminVouchersPage/TOGGLE_VOUCHER_STATUS_SUCCESS';
export const TOGGLE_VOUCHER_STATUS_ERROR = 'app/AdminVouchersPage/TOGGLE_VOUCHER_STATUS_ERROR';

// ================ Reducer ================ //

const initialState = {
  vouchers: [],
  fetchVouchersInProgress: false,
  fetchVouchersError: null,
  createVoucherInProgress: false,
  createVoucherError: null,
  updateVoucherInProgress: false,
  updateVoucherError: null,
  deleteVoucherInProgress: false,
  deleteVoucherError: null,
  toggleStatusInProgress: false,
  toggleStatusError: null,
};

export default function adminVouchersPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_VOUCHERS_REQUEST:
      return { ...state, fetchVouchersInProgress: true, fetchVouchersError: null };
    case FETCH_VOUCHERS_SUCCESS:
      return { ...state, fetchVouchersInProgress: false, vouchers: payload };
    case FETCH_VOUCHERS_ERROR:
      return { ...state, fetchVouchersInProgress: false, fetchVouchersError: payload };

    case CREATE_VOUCHER_REQUEST:
      return { ...state, createVoucherInProgress: true, createVoucherError: null };
    case CREATE_VOUCHER_SUCCESS:
      return {
        ...state,
        createVoucherInProgress: false,
        vouchers: [...state.vouchers, payload],
      };
    case CREATE_VOUCHER_ERROR:
      return { ...state, createVoucherInProgress: false, createVoucherError: payload };

    case UPDATE_VOUCHER_REQUEST:
      return { ...state, updateVoucherInProgress: true, updateVoucherError: null };
    case UPDATE_VOUCHER_SUCCESS:
      return {
        ...state,
        updateVoucherInProgress: false,
        vouchers: state.vouchers.map(v => (v.id === payload.id ? payload : v)),
      };
    case UPDATE_VOUCHER_ERROR:
      return { ...state, updateVoucherInProgress: false, updateVoucherError: payload };

    case DELETE_VOUCHER_REQUEST:
      return { ...state, deleteVoucherInProgress: true, deleteVoucherError: null };
    case DELETE_VOUCHER_SUCCESS:
      return {
        ...state,
        deleteVoucherInProgress: false,
        vouchers: state.vouchers.filter(v => v.id !== payload),
      };
    case DELETE_VOUCHER_ERROR:
      return { ...state, deleteVoucherInProgress: false, deleteVoucherError: payload };

    case TOGGLE_VOUCHER_STATUS_REQUEST:
      return { ...state, toggleStatusInProgress: true, toggleStatusError: null };
    case TOGGLE_VOUCHER_STATUS_SUCCESS:
      return {
        ...state,
        toggleStatusInProgress: false,
        vouchers: state.vouchers.map(v => (v.id === payload.id ? payload : v)),
      };
    case TOGGLE_VOUCHER_STATUS_ERROR:
      return { ...state, toggleStatusInProgress: false, toggleStatusError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchVouchersRequest = () => ({ type: FETCH_VOUCHERS_REQUEST });
export const fetchVouchersSuccess = vouchers => ({ type: FETCH_VOUCHERS_SUCCESS, payload: vouchers });
export const fetchVouchersError = error => ({ type: FETCH_VOUCHERS_ERROR, payload: error });

export const createVoucherRequest = () => ({ type: CREATE_VOUCHER_REQUEST });
export const createVoucherSuccess = voucher => ({ type: CREATE_VOUCHER_SUCCESS, payload: voucher });
export const createVoucherError = error => ({ type: CREATE_VOUCHER_ERROR, payload: error });

export const updateVoucherRequest = () => ({ type: UPDATE_VOUCHER_REQUEST });
export const updateVoucherSuccess = voucher => ({ type: UPDATE_VOUCHER_SUCCESS, payload: voucher });
export const updateVoucherError = error => ({ type: UPDATE_VOUCHER_ERROR, payload: error });

export const deleteVoucherRequest = () => ({ type: DELETE_VOUCHER_REQUEST });
export const deleteVoucherSuccess = voucherId => ({ type: DELETE_VOUCHER_SUCCESS, payload: voucherId });
export const deleteVoucherError = error => ({ type: DELETE_VOUCHER_ERROR, payload: error });

export const toggleVoucherStatusRequest = () => ({ type: TOGGLE_VOUCHER_STATUS_REQUEST });
export const toggleVoucherStatusSuccess = voucher => ({ type: TOGGLE_VOUCHER_STATUS_SUCCESS, payload: voucher });
export const toggleVoucherStatusError = error => ({ type: TOGGLE_VOUCHER_STATUS_ERROR, payload: error });

// ================ Thunks ================ //

export const fetchVouchers = params => (dispatch, getState, sdk) => {
  dispatch(fetchVouchersRequest());

  const { status, search } = params || {};
  const queryParams = new URLSearchParams();
  
  if (status) queryParams.append('status', status);
  if (search) queryParams.append('search', search);

  const path = `/api/vouchers${queryParams.toString() ? `?${queryParams}` : ''}`;

  return get(path)
    .then(data => {
      dispatch(fetchVouchersSuccess(data.data || []));
      return data;
    })
    .catch(e => {
      const error = storableError(e);
      dispatch(fetchVouchersError(error));
      throw error;
    });
};

export const createVoucher = voucherData => (dispatch, getState, sdk) => {
  dispatch(createVoucherRequest());

  return post('/api/vouchers', voucherData)
    .then(data => {
      dispatch(createVoucherSuccess(data.data));
      return data;
    })
    .catch(e => {
      const error = storableError(e);
      dispatch(createVoucherError(error));
      throw error;
    });
};

export const updateVoucher = (voucherId, voucherData) => (dispatch, getState, sdk) => {
  dispatch(updateVoucherRequest());

  return patch(`/api/vouchers/${voucherId}`, voucherData)
    .then(data => {
      dispatch(updateVoucherSuccess(data.data));
      return data;
    })
    .catch(e => {
      const error = storableError(e);
      dispatch(updateVoucherError(error));
      throw error;
    });
};

export const deleteVoucher = voucherId => (dispatch, getState, sdk) => {
  dispatch(deleteVoucherRequest());

  return del(`/api/vouchers/${voucherId}`)
    .then(() => {
      dispatch(deleteVoucherSuccess(voucherId));
    })
    .catch(e => {
      const error = storableError(e);
      dispatch(deleteVoucherError(error));
      throw error;
    });
};

export const toggleVoucherStatus = (voucherId, currentStatus) => (dispatch, getState, sdk) => {
  dispatch(toggleVoucherStatusRequest());

  // Convert current status to action: ACTIVE -> disable, DISABLED -> enable
  const action = currentStatus === 'ACTIVE' ? 'disable' : 'enable';

  return post(`/api/vouchers/${voucherId}/${action}`, {})
    .then(data => {
      dispatch(toggleVoucherStatusSuccess(data.data));
      return data;
    })
    .catch(e => {
      const error = storableError(e);
      dispatch(toggleVoucherStatusError(error));
      throw error;
    });
};

export const loadData = () => {
  return fetchCurrentUser();
};
